import React from 'react';
import { arrayOf, string, shape, objectOf, func, bool } from 'prop-types';
import { Typography } from '@mui/material';
import { GridRow } from '@mui/x-data-grid-pro';

import { Checkbox, InlineRoute, LogTable } from '../../../../../../../../components';
import { featureSetName } from '../../../../../../../../services/featureGate';
import { Banner, BannerRow } from './styles';

const MAX_SELECTION = 10;

const ConnectedVehiclesTable = ({
  applicationId,
  organization: {
    featureSetId,
  },
  filterValues: {
    mode,
  },
  selectedUserVehicles,
  setSelectedUserVehicles,
  canSubscribeVehicles,
  ...logTableProps
}) => {
  const isFreeFeatureSet = featureSetName(featureSetId) === 'free';
  const isLiveMode = mode === 'live';
  const showFreePlanBanner = isFreeFeatureSet && isLiveMode;

  const reachedSelectionLimit = Object.keys(selectedUserVehicles).length >= MAX_SELECTION;

  const handleRowClick = (row) => {
    if (showFreePlanBanner && row.vehicleId !== logTableProps.pageInfo.freePlanActiveVehicle) {
      return;
    }

    setSelectedUserVehicles((prev) => {
      const updatedSelection = { ...prev };
      const { vehicle, userId, vehicleId } = row;
      const userVehicleId = `${userId}:${vehicleId}`;

      if (updatedSelection[userVehicleId]) {
        delete updatedSelection[userVehicleId];
      } else if (!reachedSelectionLimit) {
        updatedSelection[userVehicleId] = {
          vehicleId,
          userId,
          displayName: [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' '),
        };
      }
      return updatedSelection;
    });
  };

  const allCurrentRowsSelected = logTableProps.pageInfo.rows.length > 0
    && logTableProps.pageInfo.rows.every((row) => {
      const userVehicleId = `${row.userId}:${row.vehicleId}`;
      return selectedUserVehicles[userVehicleId];
    });

  const handleToggleAll = (event) => {
    setSelectedUserVehicles(
      event.target.checked
        ? logTableProps.pageInfo.rows.reduce((acc, row) => {
          const { vehicle, userId, vehicleId } = row;
          const userVehicleId = `${userId}:${vehicleId}`;
          acc[userVehicleId] = {
            vehicleId,
            userId,
            displayName: [vehicle.year, vehicle.make, vehicle.model].join(' '),
          };
          return acc;
        }, {})
        : {},
    );
  };

  const modifiedColumns = canSubscribeVehicles ? [
    {
      field: 'checkbox',
      headerName: '',
      width: 60,
      disableColumnMenu: true,
      hideable: false,
      pinnable: false,
      sortable: false,
      resizable: false,
      cellClassName: 'p-none',
      headerClassName: 'p-none',
      renderHeader: showFreePlanBanner ? null : () => (
        <div style={{ marginLeft: '24px' }}>
          <Checkbox
            id="select-all-vehicles"
            checked={allCurrentRowsSelected}
            onChange={handleToggleAll}
          />
        </div>
      ),
    },
    ...logTableProps.pageInfo.columns.map((column) => {
      if (column.field === 'vehicle') {
        const vehicleCount = Object.keys(selectedUserVehicles).length;
        return {
          ...column,
          headerName: vehicleCount ? `Vehicle (${vehicleCount} of ${MAX_SELECTION} selected)` : 'Vehicle',
          cellClassName: 'p-l-none',
          headerClassName: 'p-l-none',
        };
      }
      return column;
    }),
  ] : logTableProps.pageInfo.columns;
  // For the free plan, we show a banner indicating that they only have access to 1 vehicle
  let decoratedRows = showFreePlanBanner ? (() => {
    const activeVehicleRow = logTableProps.pageInfo.rows.find(
      row => row.vehicleId === logTableProps.pageInfo.freePlanActiveVehicle,
    );

    const banner = {
      vehicleId: `${logTableProps.pageInfo.freePlanActiveVehicle}-banner`,
      isBanner: true,
    };

    const remainingVehicles = logTableProps.pageInfo.rows.filter(row =>
      row.vehicleId !== logTableProps.pageInfo.freePlanActiveVehicle,
    ).map(row => ({
      ...row,
      isGated: true,
    }));

    return [
      ...(activeVehicleRow ? [activeVehicleRow] : []),
      banner,
      ...remainingVehicles,
    ];
  })() : logTableProps.pageInfo.rows;

  if (canSubscribeVehicles) {
    decoratedRows = decoratedRows.map((row) => {
      const checked = Boolean(selectedUserVehicles[`${row.userId}:${row.vehicleId}`]);
      return {
        ...row,
        checkbox: {
          checked,
          disabled: Boolean((!checked && reachedSelectionLimit) || row.isGated),
          handleChange: null, // let the event propagate to handleRowClick
        },
      };
    });
  }

  const customRowRender = (params) => {
    if (params.row.isBanner) {
      return (
        <BannerRow {...params}>
          <Banner severity="gated">
            <Typography variant="body1">
              Your current plan allows access of up to <b>1 vehicle.</b>
              <InlineRoute
                text=" |+|Upgrade now@@/team/billing|+| to access more vehicles."
              />
            </Typography>
          </Banner>
        </BannerRow>
      );
    }

    return (
      <GridRow
        {...params}
        style={
          showFreePlanBanner &&
          params.row.vehicleId !== logTableProps.pageInfo.freePlanActiveVehicle &&
          { opacity: 0.5 }
        }
      />
    );
  };

  return (
    <LogTable
      {...logTableProps}
      pageInfo={{
        ...logTableProps.pageInfo,
        columns: modifiedColumns,
        rows: decoratedRows,
      }}
      customRowRender={customRowRender}
      applicationId={applicationId}
      handleRowClick={canSubscribeVehicles ? handleRowClick : null}
    />
  );
};

export default ConnectedVehiclesTable;


ConnectedVehiclesTable.propTypes = {
  organization: shape({
    featureSetId: string.isRequired,
    rolePermissions: arrayOf(string).isRequired,
  }).isRequired,
  applicationId: string.isRequired,
  filterValues: shape({
    mode: string.isRequired,
  }).isRequired,
  selectedUserVehicles: objectOf(shape({
    userId: string.isRequired,
    vehicleId: string.isRequired,
  })).isRequired,
  setSelectedUserVehicles: func.isRequired,
  canSubscribeVehicles: bool.isRequired,
};

